/* eslint-disable react-hooks/rules-of-hooks */
import PhotoGalleryCard from "@blocks/Card/PhotoGalleryCard";
import MainBlock from "@container/MainBlock";
import OutbrainMainContainer, {
  AdsSettings,
} from "@container/OutbrainMainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import Taboola from "@elements/Advertisement/Taboola";
import { Imu1CompanionAdsInfiniteScroll } from "@elements/Advertisement/variant/AdsCompanion";
import fetchTMApi from "@helper/fetchTMApi";
import { displayAfter } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import { PhotoGalleryMetaData } from "@pages/PhotoGallery/PhotoGalleryMetaData";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import {
  CustomContext,
  ResponseType,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { Fragment, useState } from "react";

const PAGE_SIZE = 7;
const INITIAL_ARTICLE_COUNT = 7;

export default function PhotoGallery() {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<ProcessedArticleData[], string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const adSettings: AdsSettings = useGlobalAdSetting();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const sectionArticles = dataLoaderResponse.payload;

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = sectionArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(sectionArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? sectionArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: sectionArticles[0].sectionName,
      sort: lastLoadedArticle && lastLoadedArticle.sort,
    };

    const fetchMoreStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      try {
        const queryParams = new URLSearchParams({
          section: requestData.filterArticlesBasedOn as string,
          sort: Array.isArray(requestData.sort)
            ? requestData.sort.join(",")
            : "",
          size: "7",
        }).toString();
        const response: ProcessedArticleData[] = await fetchTMApi(
          `more-section-stories?${queryParams}`,
          "GET",
        );

        return response;
      } catch (error) {
        return [];
      }
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };

  let sectionName = "";
  const sectionTamilName = "படச் செய்திகள்";
  if (sectionArticles[0].sectionName) {
    sectionName = sectionArticles[0].sectionName;
  }

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  const { gaData, metaTags } = PhotoGalleryMetaData({
    sectionName,
    sectionTamilName,
  });

  return (
    <OutbrainMainContainer
      gaData={gaData}
      metaTagsProps={metaTags}
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock sectionName={sectionName}>
          <div className="flex flex-wrap">
            {[...sectionArticles, ...loadMoreData].map((section, index) => (
              <Fragment key={section.id}>
                {displayAfter(index, PAGE_SIZE) && (
                  <>
                    <div className="w-full my-2">
                      {adSettings.isTaboolaEnabled && (
                        <>
                          <div className="border-t border-t-gray-2000 mt-2"></div>
                          <Taboola
                            counter={index / PAGE_SIZE}
                            pathName={"/photo-gallery"}
                            pageType="article"
                            mode="thumbnails-base-widget"
                            container="below-article-thumbnails"
                            placement="Below Article Thumbnails"
                            targetType="mix"
                          />
                        </>
                      )}
                    </div>
                    <Imu1CompanionAdsInfiniteScroll
                      sectionName={sectionName}
                      index={index / PAGE_SIZE}
                      pageAdTargetType={pageAdTargetValue}
                    />
                  </>
                )}
                <PhotoGalleryCard
                  displayWidth={
                    (index % INITIAL_ARTICLE_COUNT) % 3 == 0 ? 748 : 371
                  }
                  displayHeight={
                    (index % INITIAL_ARTICLE_COUNT) % 3 == 0 ? 499 : 247
                  }
                  {...section}
                  overrideStyle={{
                    cardWrapper:
                      (index % INITIAL_ARTICLE_COUNT) % 3 == 0
                        ? "lg:flex-100 py-1"
                        : (index % INITIAL_ARTICLE_COUNT) % 3 == 1
                          ? "lg:flex-50 pr-0.5"
                          : (index % INITIAL_ARTICLE_COUNT) % 3 == 2
                            ? "lg:flex-50 pl-0.5"
                            : "",
                  }}
                />
              </Fragment>
            ))}
            {showLoadMore ? (
              <div className="flex w-full justify-center mb-xl">
                <LoadMore
                  rootClassName="my-4 w-full"
                  onLoadMore={handleLoadMore}
                  hasMore={hasMoreStories}
                  loadText="Load More"
                  pageNumber={pageNumber}
                  sectionName={sectionName}
                />
              </div>
            ) : null}
          </div>
        </MainBlock>
        <SideBlock
          sectionName={sectionName}
          className="w-full lg:block"
          pageAdTargetValue={pageAdTargetValue}
        />
      </WrapperBlock>
    </OutbrainMainContainer>
  );
}
